import React, { Fragment, useState, useEffect, memo } from 'react'
import { useQueryParam, StringParam } from 'use-query-params'
import useAxios from 'axios-hooks'
import { useProjects } from 'context/project-context'
import { getAuthHeader } from 'utils/auth'
import { Table } from 'antd'
import { NoData, PageAlert, TableContainer } from 'components'
import ScanSettingsListFilters from 'views/ScanSettings/ScanSettingsView/components/ScanSettingsListFilters'
import ScanSettingsListColumns from 'views/ScanSettings/ScanSettingsView/components/ScanSettingsListColumns'
import ScanSettingsTop from 'views/ScanSettings/ScanSettingsView/components/ScanSettingsTop'
import { notificationToast } from 'components/NotificationToast';
import { useUser } from 'context/user-context'
import { BASE_ADDRESS_KEY } from 'views/ScanSettings/NewScan/constants'
import styled from '@emotion/styled'

const StyledTable = styled(Table)`
	min-width: 1200px;
    th:nth-child(5){
        min-width: 130px;
	}
	th:nth-child(6){
        min-width: 120px;
    }
	th:nth-child(7){
        min-width: 130px;
    }
`;

const filterBySearch = (data, searchTerm) => {
	if (!searchTerm) return data
	return data.filter(d => d.name.toLowerCase().includes(searchTerm))
}


function ScanSettingsList({ data, loading, error, onFilterChange, navigate, getScanSettingsList }) {
	const [search, setSearch] = useQueryParam('search', StringParam)
	const [filteredData, setFilteredData] = useState(null)
	const [item, setItem] = useState({})
	const [isParametersModalVisible, setParametersModalVisible] = useState(false);

	const { project } = useProjects()
	const user = useUser();

	const onSearch = e => {
		let val = e.target.value.toLowerCase()
		setSearch(val)
		setFilteredData(filterBySearch(data, val))
	}

	const [{ data: deleteData, loading: deleteLoading, error: deleteError }, deleteScan] = useAxios(
		{
			method: 'DELETE',
			headers: getAuthHeader(),
			validateStatus: false
		},
		{ manual: true }
	)

	const [{ data: startData, loading: startLoading, error: startError }, scan] = useAxios({
		url: `${project.name}/scan-settings/scans`,
		method: 'POST',
		headers: getAuthHeader()
	}, {manual: true})

	useEffect(() => {
		if (data) {
			setFilteredData(filterBySearch(data, search))
		}
	}, [data, search])

	useEffect(() => {
		if(startData) {
			navigate(`${item.id}/scans/`)
			setParametersModalVisible(false);
		}
	}, [startData])

	useEffect(() => {

		if (deleteData && deleteData.Errors && deleteData.Errors.length) {
			openNotification(deleteData.Errors)
		}
		else if(deleteData === "") {
			openNotification()
			getScanSettingsList({data: {}})
		}
	}, [deleteData])

	useEffect(() => {
		if(startError){
			let scanError = startError && 
				startError.response && 
				startError.response.data && 
				startError.response.data.Errors;

			notificationToast({
				message: 'Error',
				description: scanError,
				icontype: "close"
			})
		}
	}, [startError])

	const openNotification = (errors) => {
		if (!errors) {
			notificationToast({
				message: 'Success',
				description: `'${item.name}' was deleted succesfully`,
				icontype: "check"
			})
		} else {
			notificationToast({
				message: 'Error',
				description: `Could not delete '${item.name}'. ${errors.join()}`,
				icontype: "alert"
			})
		}
	}

	const handleStartScan = (item,  baseAddress, uRemediateProfile) => {
		setItem(item);
		let data = {scan_settings_id: item.id}

		if(baseAddress){
			data.parameters = {[BASE_ADDRESS_KEY]: baseAddress}
		}

		if(uRemediateProfile){
			if(data.parameters){
				data.parameters["disable_uremediate_profiles"] = uRemediateProfile;
			}
			else{
				data.parameters = {"disable_uremediate_profiles": uRemediateProfile}
			}
		}

		scan({data}).catch(e => {})
	}

	const handleDeleteScan = (e, item) => {
		setItem(item)
		deleteScan({url: `${project.name}/scan-settings/${item.id}`})
	}

	const scans = filteredData ? filteredData : data

	const errors = startError && 
				startError.response && 
				startError.response.data && 
				startError.response.data.Errors;
	return (
		<Fragment>
			{error && <PageAlert message={error} />}
			<ScanSettingsTop navigate={navigate} user={user}/>
			<ScanSettingsListFilters data={scans} onSearch={onSearch} onFilterChange={onFilterChange} />
			<TableContainer>
				<StyledTable
					rowKey="id"
					pagination={{ hideOnSinglePage: true }}
					columns={ScanSettingsListColumns({handleDeleteScan, handleStartScan, user, isParametersModalVisible, setParametersModalVisible, errors, startData})}
					dataSource={scans}
					loading={loading}
					locale={{
						emptyText: <NoData />
					}}
				/>
			</TableContainer>
		</Fragment>
	)
}

export default memo(ScanSettingsList)
